import { useState } from "react";
import dayjs from "dayjs";

import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Popover from "react-bootstrap/Popover";
import Table from "react-bootstrap/Table";
import { FaEllipsisV, FaEye, FaInfoCircle } from "react-icons/fa";

import { formatIdentifier } from "@/utils/formatIdentifier";
import { handleColumnToggle } from "@/utils/handleColumnTogle";
import { getAge } from "@/utils/dateUtil";
import { ResumeCitizenProceduresIndicatorB5Data } from "@/services/esus/indicator/esb/b5/getResumeCitizensProceduresIndicatorB5";

import { TableSkeletons } from "@/components/TableSkeletons";
import { CardOverlayTrigger } from "@/components/CardOverlayTrigger";

type Column = {
	title: string;
	visible: boolean;
};

type IndicatorEsbB5AttendanceTableColumns = {
	citizenName: Column;
	citizenAge: Column;
	document: Column;
	microarea: Column;
	citizenEsfHealthTeam: Column;
	attendanceEsbHealthTeam: Column;
	attendanceEsfHealthTeam: Column;
	totalPreventProcedures: Column;
	totalProcedures: Column;
};

type IndicatorEsbB5CitizensTableProps = {
	citizensWithProcedures: ResumeCitizenProceduresIndicatorB5Data[];
	isLoading: boolean;
	openCitizenHistory: (citizenId: number, ine?: string) => void;
};

export function IndicatorEsbB5CitizensTable({
	citizensWithProcedures,
	isLoading,
	openCitizenHistory,
}: Readonly<IndicatorEsbB5CitizensTableProps>) {
	const [columns, setColumns] = useState<IndicatorEsbB5AttendanceTableColumns>({
		citizenName: { title: "Nome do cidadão", visible: true },
		citizenAge: { title: "Idade", visible: true },
		document: { title: "Documento", visible: true },
		microarea: { title: "MA", visible: true },
		citizenEsfHealthTeam: { title: "Equipe ESF Cidadão", visible: true },
		attendanceEsbHealthTeam: { title: "Equipe ESB Atendimento", visible: true },
		attendanceEsfHealthTeam: { title: "Equipe ESF vinculada a ESB Atendimento", visible: true },
		totalPreventProcedures: { title: "Procedimentos Preventivos", visible: true },
		totalProcedures: { title: "Procedimentos", visible: true },
	});

	return (
		<Table striped bordered hover responsive>
			<thead className="text-center fs-7">
				<tr className="fw-bolder text-center">
					<th className={`text-start px-2 ${!columns.citizenName.visible && "d-none"}`}>
						{columns.citizenName.title}
					</th>
					<th className={`px-2 ${!columns.citizenAge.visible && "d-none"}`}>
						{columns.citizenAge.title}
					</th>
					<th className={`px-2 ${!columns.document.visible && "d-none"}`}>
						{columns.document.title}
					</th>
					<th className={`px-2 ${!columns.microarea.visible && "d-none"}`}>
						{columns.microarea.title}
					</th>
					<th
						className={`text-start px-2 ${
							!columns.citizenEsfHealthTeam.visible && "d-none"
						}`}
					>
						{columns.citizenEsfHealthTeam.title}
					</th>
					<th
						className={`text-start px-2 ${
							!columns.attendanceEsbHealthTeam.visible && "d-none"
						}`}
					>
						{columns.attendanceEsbHealthTeam.title}
					</th>
					<th
						className={`text-start px-2 ${
							!columns.attendanceEsfHealthTeam.visible && "d-none"
						}`}
					>
						{columns.attendanceEsfHealthTeam.title}
					</th>
					<th className={`px-2 ${!columns.totalPreventProcedures.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.totalPreventProcedures.title}
							<CardOverlayTrigger
								header="Total de procedimentos preventivos"
								message="Quantidade de procedimentos preventivos realizados no período"
								icon={<FaInfoCircle />}
							/>
						</div>
					</th>
					<th className={`px-2 ${!columns.totalProcedures.visible && "d-none"}`}>
						<div className="d-flex flex-column">
							{columns.totalProcedures.title}
							<CardOverlayTrigger
								header="Total de procedimentos"
								message="Quantidade de procedimentos realizados no período"
								icon={<FaInfoCircle />}
							/>
						</div>
					</th>
					<th className="text-nowrap pb-0">
						Ações
						<OverlayTrigger
							trigger="click"
							placement="bottom"
							overlay={
								<Popover>
									<Popover.Body>
										<Form className="d-flex flex-column gap-2">
											{Object.keys(columns).map((key, index) => (
												<Form.Check
													key={`citizen-attendances-table-column-${
														index + 1
													}`}
													type="checkbox"
													label={
														columns[
															key as keyof IndicatorEsbB5AttendanceTableColumns
														].title
													}
													checked={
														columns[
															key as keyof IndicatorEsbB5AttendanceTableColumns
														].visible
													}
													onChange={() =>
														handleColumnToggle(
															key as keyof IndicatorEsbB5AttendanceTableColumns,
															setColumns
														)
													}
												/>
											))}
										</Form>
									</Popover.Body>
								</Popover>
							}
							rootClose
						>
							<Button
								title="Exibição de colunas da tabela"
								variant="white"
								className="px-2"
								disabled={isLoading}
							>
								<FaEllipsisV />
							</Button>
						</OverlayTrigger>
					</th>
				</tr>
			</thead>
			<tbody className="fs-8">
				{!isLoading && citizensWithProcedures.length > 0
					? citizensWithProcedures.map((citizenWithProcedures, index) => (
							<tr key={`citizen-attendances-table-row-${index + 1}`}>
								<td className={`px-2 ${!columns.citizenName.visible && "d-none"}`}>
									<div className="d-flex flex-column">
										<div>{citizenWithProcedures.fullName}</div>
										<div className="text-muted">
											{citizenWithProcedures.birthdate
												? dayjs(citizenWithProcedures.birthdate).format(
														"DD/MM/YYYY"
												  )
												: ""}
										</div>
									</div>
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.citizenAge.visible && "d-none"
									}`}
								>
									{citizenWithProcedures.birthdate
										? getAge(citizenWithProcedures.birthdate)
										: ""}
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.document.visible && "d-none"
									}`}
								>
									{citizenWithProcedures.cpf
										? formatIdentifier(citizenWithProcedures.cpf)
										: citizenWithProcedures.cns ?? ""}
								</td>
								<td
									className={`text-center text-nowrap px-2 ${
										!columns.microarea.visible && "d-none"
									}`}
								>
									{citizenWithProcedures.area ?? "FA"}
								</td>
								<td
									className={`px-2 ${
										!columns.citizenEsfHealthTeam.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>{citizenWithProcedures.healthTeamName}</div>
										<div className="text-muted">
											{citizenWithProcedures.healthTeamIne}
										</div>
									</div>
								</td>
								<td
									className={`px-2 ${
										!columns.attendanceEsbHealthTeam.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>{citizenWithProcedures.esbHealthTeamName}</div>
										<div className="text-muted">
											{citizenWithProcedures.esbHealthTeamIne}
										</div>
									</div>
								</td>
								<td
									className={`px-2 ${
										!columns.attendanceEsfHealthTeam.visible && "d-none"
									}`}
								>
									<div className="d-flex flex-column">
										<div>{citizenWithProcedures.linkedHealthTeamName}</div>
										<div className="text-muted">
											{citizenWithProcedures.linkedHealthTeamIne}
										</div>
									</div>
								</td>
								<td
									className={`text-center px-2 ${
										!columns.totalPreventProcedures.visible && "d-none"
									}`}
								>
									{citizenWithProcedures.totalPreventProcedures}
								</td>
								<td
									className={`text-center px-2 ${
										!columns.totalProcedures.visible && "d-none"
									}`}
								>
									{citizenWithProcedures.totalProcedures}
								</td>
								<td className="text-center">
									<Button
										className="bg-primary py-1 px-3"
										onClick={() =>
											openCitizenHistory(
												citizenWithProcedures.id,
												citizenWithProcedures.esbHealthTeamIne
											)
										}
										title="Histórico do cidadão"
									>
										<FaEye className="fs-4" />
									</Button>
								</td>
							</tr>
					  ))
					: isLoading && (
							<TableSkeletons
								numberOfCells={
									Object.values(columns).reduce(
										(count, column) =>
											count + (column?.visible === true ? 1 : 0),
										0
									) + 1
								}
							/>
					  )}
			</tbody>
		</Table>
	);
}
